import useAccountEnhanced from "src/hooks/use-account-enhanced";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import {
    useCompleteEnqueuedOrder,
    useCreateOrderNote,
    useEnqueueOrder,
} from "src/api/mvte-query-options";
import { useEffect, useState } from "react";
import { HiOutlineSwitchHorizontal } from "react-icons/hi";

type QueueManagerInputs = {
    queueName: string;
    note?: string;
};

export function OrderQueueManagerForm({
    orderNumber,
    billToCode,
    queueName,
    onSuccess,
    mode: fixedMode,
}: {
    orderNumber: string;
    billToCode: string;
    queueName: string;
    onSuccess: () => void;
    mode?: "complete" | "add";
}) {
    const createOrderNoteMutation = useCreateOrderNote({
        orderNumber: orderNumber,
    });
    const completeEnqueuedOrder = useCompleteEnqueuedOrder({ orderNumber });
    const enqueueOrderMutation = useEnqueueOrder({ orderNumber });
    const form = useForm<QueueManagerInputs>();
    const accountEnhanced = useAccountEnhanced();
    const [mode, setMode] = useState<"complete" | "add">("complete");

    const currentMode = fixedMode || mode;

    async function onSubmit({
        queueName: newQueueName,
        note,
    }: QueueManagerInputs) {
        if (currentMode === "complete") {
            try {
                await completeEnqueuedOrder.mutateAsync({
                    OrderNumber: orderNumber,
                    CurrentQueue: queueName,
                    EmployeeCode: accountEnhanced.me.employeeCode,
                    Payload: {
                        CustomerCode: billToCode,
                        OrderNumber: orderNumber,
                    },
                    UserPrincipalName: accountEnhanced.account.name!,
                });

                toast.success("Queue completed successfully");
            } catch (error) {
                toast.error("Failed to complete queue");
                return;
            }
        } else if (currentMode === "add") {
            try {
                await enqueueOrderMutation.mutateAsync({
                    OrderNumber: orderNumber,
                    ToQueue: newQueueName,
                    CurrentQueue: queueName,
                    EmployeeCode: accountEnhanced.me.employeeCode,
                    Payload: {
                        CustomerCode: billToCode,
                        OrderNumber: orderNumber,
                    },
                    UserPrincipalName: accountEnhanced.account.name!, // TODO: better way to ensure name is not null
                });

                toast.success("Queue updated successfully");
            } catch (error) {
                toast.error("Failed to update queue");
                return;
            }
        }

        if (note) {
            await createOrderNoteMutation.mutateAsync({
                EmployeeCode: accountEnhanced.me.employeeCode.toUpperCase(),
                NoteText: note,
                NoteType:
                    currentMode === "complete" ? queueName! : newQueueName,
                Payload: {
                    CustomerCode: billToCode ?? "",
                    OrderNumber: orderNumber ?? "",
                },
                UserPrincipalName: accountEnhanced.account.name ?? "",
            });

            toast.success("Note added successfully");
        }

        form.reset();

        onSuccess();
    }

    // Reset states
    useEffect(() => {
        if (!fixedMode) {
            setMode("complete");
        }
    }, [orderNumber, fixedMode]);

    useEffect(() => {
        form.reset();
    }, [orderNumber, form]);

    return (
        <form onSubmit={form.handleSubmit(onSubmit)}>
            {/* Order Number */}
            <fieldset className="fieldset w-full">
                <legend className="fieldset-legend">Order Number</legend>
                <input
                    type="text"
                    defaultValue={orderNumber}
                    readOnly
                    className="input input-bordered input-sm w-full"
                />
            </fieldset>
            {/* Bill To Code */}
            <fieldset className="fieldset w-full">
                <legend className="fieldset-legend">Bill To Code</legend>
                <input
                    type="text"
                    defaultValue={billToCode}
                    readOnly
                    className="input input-bordered input-sm w-full"
                />
            </fieldset>
            {/* User Name */}
            <fieldset className="fieldset w-full">
                <legend className="fieldset-legend">User Name</legend>
                <input
                    type="text"
                    defaultValue={accountEnhanced.me.employeeCode}
                    readOnly
                    className="input input-bordered input-sm w-full"
                />
            </fieldset>
            {/* Queue Name */}
            <fieldset className="fieldset w-full">
                <legend className="fieldset-legend">Queue Name</legend>
                {currentMode === "complete" ? (
                    <input
                        type="text"
                        defaultValue={queueName}
                        readOnly
                        className="input input-bordered input-sm w-full"
                    />
                ) : (
                    <select
                        className="select select-bordered select-sm w-full"
                        {...form.register("queueName", { required: true })}
                    >
                        <option value="">Select Queue</option>

                        {["Billing Hold", "Rating", "Customer Account", "Fleet"]
                            .filter((queue) => queue !== queueName)
                            .map((queue) => (
                                <option value={queue}>{queue}</option>
                            ))}
                    </select>
                )}
            </fieldset>

            {/* Note */}
            <fieldset className="fieldset">
                <legend className="fieldset-legend">Note</legend>
                <textarea
                    className="textarea textarea-bordered textarea-sm h-24 resize-none w-full"
                    placeholder="Note..."
                    {...form.register("note")}
                ></textarea>
            </fieldset>

            <div className="flex justify-between items-center mt-4">
                <button
                    type="submit"
                    className="btn btn-neutral btn-sm"
                    disabled={form.formState.isSubmitting || !orderNumber}
                >
                    {form.formState.isSubmitting ? (
                        <>
                            <span className="loading loading-spinner"></span>
                            loading
                        </>
                    ) : (
                        { add: "Send to Queue", complete: "Complete Queue" }[
                            currentMode
                        ]
                    )}
                </button>

                {!fixedMode && currentMode === "complete" && (
                    <button
                        className="flex items-center space-x-2 cursor-pointer"
                        type="button"
                        onClick={() => setMode("add")}
                    >
                        <span>Send to Queue</span>
                        <HiOutlineSwitchHorizontal />
                    </button>
                )}

                {!fixedMode && currentMode === "add" && (
                    <button
                        className="flex items-center space-x-2 cursor-pointer"
                        type="button"
                        onClick={() => setMode("complete")}
                    >
                        <span>Complete Queue</span>
                        <HiOutlineSwitchHorizontal />
                    </button>
                )}
            </div>
        </form>
    );
}
